import React, { useEffect, useMemo, useState } from "react";

import {
  TFunction,
  createFootprintCalculationSummary,
  formatFractionInput,
  formatNumber,
  isPercentageComplete,
  queryIsLoading,
  useDeleteFootprintingCropByPrimaryDataId,
  useGetBasicDetailsForManyFields,
  useGetDairyFeedTypes,
  useGetFarmDetails,
  useGetFootprintingCalculatedCropFootprints,
  useGetFootprintingCalculatedFarmFootprints,
  useGetFootprintingCropFootprint,
  useGetFootprintingCropPrimaryDataById,
  useGetFootprintingDairyFeedInformationByPrimaryDataId,
  useGetFootprintingDairyFootprint,
  useGetFootprintingDairyPrimaryDataById,
  useGetFootprintingFarmFootprint,
  useGetFootprintingFarmPrimaryDataById,
  useGetPracticeCrop,
  useNavigate,
  useTranslation,
} from "utils";

import { isMainCommodity } from "consts";

import {
  AlertBanner,
  Button,
  Card,
  CardContentGroup,
  ContextMenu,
  Divider,
  ErrorBoundary,
  Flex,
  FormCardButtonAddition,
  Icon,
  ItemProps,
  LazyLoadImage,
  Modal,
  Skeleton,
  TagColour,
  Tooltip,
  Message as message,
} from "components";

import { FootprintingDairyFeedProductionDrawer } from "pages/app/footprinting/components/FootprintingDairyFeedProductionDrawer";
import { CropFormFlow } from "pages/app/footprinting/components/formFlows/CropFormFlow";
import { DairyFormFlow } from "pages/app/footprinting/components/formFlows/DairyFormFlow";
import { FarmFormFlow } from "pages/app/footprinting/components/formFlows/FarmFormFlow";

interface FootprintingFormButton {
  year: number;
  farmId: string;
  cropId?: string;
  isDairy?: boolean;
  yearIsSubmitted?: boolean;
}

type CardType = "crop" | "dairy" | "farm";

export const FootprintingLoadingState = ({ cardType }: { cardType: CardType }) => {
  const config = {
    showImage: cardType === "crop",
    showCO2Display: cardType === "crop",
  };

  return (
    <Card className="padding-125 width-100 margin-top-100 footprint-button-card">
      <Flex gap="28px" align="center">
        {config.showImage && (
          <div className="crop-image-container">
            <Skeleton type="block" height="48px" width="48px" className="skeleton-rectangular" />
          </div>
        )}

        <Flex className="width-100" vertical gap="8px">
          <div className="skeleton-containers form-title margin-bottom-25">
            <Skeleton type="title" width="180px" className="skeleton-rectangular" />
          </div>
          <div className="skeleton-containers form-description margin-bottom-25">
            <Skeleton type="title" width="240px" className="skeleton-rectangular" />
          </div>
        </Flex>

        {config.showCO2Display && (
          <div className="skeleton-containers co2-display">
            <Skeleton type="block" height="18px" width="60px" className="skeleton-rectangular" />
          </div>
        )}

        <div className="skeleton-containers progress">
          <Skeleton type="block" height="18px" width="40px" className="skeleton-rectangular" />
        </div>

        <div className="skeleton-containers">
          <Skeleton type="block" height="32px" width="80px" className="skeleton-rectangular" />
        </div>
      </Flex>
    </Card>
  );
};

export const getTagProps = (
  t: TFunction,
  isSubmitted: boolean,
  isComplete: boolean,
  farmCardCompletion: boolean,
  preliminaryResult: boolean,
  cardType: "crop" | "dairy" | "farm",
  completionPct: number
): { text: string; colour: TagColour; tooltip?: string }[] => {
  const tags: { text: string; colour: TagColour; tooltip?: string }[] = [];

  const addTag = (text: string, colour: TagColour, tooltip?: string) => {
    tags.push({ text: t(text), colour, tooltip: tooltip ? t(tooltip) : undefined });
  };

  if (completionPct === 0 && cardType === "farm") {
    addTag("formButtons.tag.startHere", "orange", "formButtons.tooltips.tooltipFarm");
  }

  if (isComplete && !farmCardCompletion) {
    addTag("formButtons.tag.farmDataMissing", "orange", "formButtons.tooltips.tooltipFarmDataMissing");
  }

  if (((isSubmitted && preliminaryResult) || isSubmitted) && cardType === "crop") {
    addTag("formButtons.tag.preliminaryResult", "yellow", "formButtons.tooltips.tooltipPreliminaryResult");
  }

  return tags;
};

interface FootprintingFormButtonProps {
  farmId: string;
  cropId?: string;
  year: number;
  cardType: CardType;
  yearIsSubmitted: boolean;
  initialStep?: number;
  onFormFlowClose?: () => void;
}

const FootprintingFormButton = ({ farmId, year, cardType, cropId, yearIsSubmitted, initialStep, onFormFlowClose }: FootprintingFormButtonProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["footprinting", "dairy"]);
  const [showFormFlow, setShowFormFlow] = useState(false);
  const farmDetailsQuery = useGetFarmDetails({ farmId });
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [showRemoveAssessmentModal, setShowRemoveAssessmentModal] = useState(false);
  const [selectedFeedTypeId, setSelectedFeedTypeId] = useState<string | null>(null);

  const cropInfo = useGetPracticeCrop(cropId);
  const farmProgressQuery = useGetFootprintingFarmFootprint(farmId, year, { enabled: cardType !== "crop" });
  const farmPrimaryDataQuery = useGetFootprintingFarmPrimaryDataById(farmProgressQuery.data?.footprint?.id ?? "");
  const cropProgressQuery = useGetFootprintingCropFootprint(farmId, cropId ?? "", year, { enabled: cardType === "crop" && cropId !== undefined });
  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(cropProgressQuery.data?.footprint?.id ?? "");
  const dairyProgressQuery = useGetFootprintingDairyFootprint(farmId, year, { enabled: cardType === "dairy" });
  const dairyPrimaryDataQuery = useGetFootprintingDairyPrimaryDataById(dairyProgressQuery.data?.footprint?.id ?? "");
  const dairyFeedTypesQuery = useGetDairyFeedTypes({ enabled: cardType === "dairy" });
  const dairyFeedInformationQuery = useGetFootprintingDairyFeedInformationByPrimaryDataId(dairyProgressQuery.data?.footprint?.id ?? "");
  const farmCalculationQueries = useGetFootprintingCalculatedFarmFootprints(farmId, [year], {
    enabled: isPercentageComplete(farmProgressQuery.data?.completionPct ?? 0),
  });
  const cropCalculationQueries = useGetFootprintingCalculatedCropFootprints(farmId, [year], [cropId ?? ""], {
    enabled: cardType === "crop" && isPercentageComplete(cropProgressQuery.data?.completionPct ?? 0),
  });
  const fieldIds = useMemo(() => cropProgressQuery.data?.footprint?.fieldIds ?? [], [cropProgressQuery.data?.footprint?.fieldIds]);
  const fieldBasicDetailsQueries = useGetBasicDetailsForManyFields(fieldIds, {
    enabled: cardType === "crop" && fieldIds.length > 0,
    includeDisabledFields: true,
  });
  const deleteFootprintingCropByPrimaryDataId = useDeleteFootprintingCropByPrimaryDataId(farmId, cropId ?? "", year, cropProgressQuery.data?.footprint?.id);

  const footprintDisplayData = useMemo(
    () =>
      cardType === "crop" && farmDetailsQuery.data && farmCalculationQueries[0].data && cropCalculationQueries[0].data && cropProgressQuery.data?.footprint
        ? createFootprintCalculationSummary(
            farmDetailsQuery.data,
            fieldBasicDetailsQueries.map((fbdq) => fbdq.data).filter((d) => d !== undefined),
            farmCalculationQueries[0].data,
            cropCalculationQueries[0].data,
            cropProgressQuery.data.footprint
          )
        : null,
    [cropCalculationQueries, cropProgressQuery?.data?.footprint, farmCalculationQueries, farmDetailsQuery.data, fieldBasicDetailsQueries, cardType]
  );

  const feedTypesWithCompletion = useMemo(() => {
    if (!dairyFeedInformationQuery.data || !dairyFeedTypesQuery.data) {
      return [];
    }

    return dairyFeedInformationQuery.data.flatMap((feedData) => {
      const feedType = dairyFeedTypesQuery.data.find((ft) => ft.id === feedData.feedTypeId);

      if (!feedType || feedType.sourceType !== "grown") return [];

      return [
        {
          feedTypeId: feedType.id,
          feedInformationId: feedData.id ?? "",
          name: feedType.name,
          translationSlug: feedType.translationSlug,
          isComplete: dairyProgressQuery.data?.feedProductionCompletion?.[feedData.id ?? ""] ?? false,
        },
      ];
    });
  }, [dairyFeedInformationQuery.data, dairyFeedTypesQuery.data, dairyProgressQuery.data?.feedProductionCompletion]);

  // Note: We DO NOT care if the ...PrimaryData query is loading
  const loading =
    queryIsLoading(farmDetailsQuery) ||
    fieldBasicDetailsQueries.some(queryIsLoading) ||
    (cardType === "crop"
      ? cropCalculationQueries.some(queryIsLoading) || queryIsLoading(cropProgressQuery)
      : cardType === "dairy"
      ? queryIsLoading(dairyProgressQuery)
      : queryIsLoading(farmProgressQuery));

  // Open form flow when initialStep is provided
  useEffect(() => {
    if (initialStep !== undefined && cardType === "crop") {
      openFormFlow();
    }
  }, [initialStep]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!showFormFlow && loading) {
    return <FootprintingLoadingState cardType={cardType} />;
  }

  const handleExit = () => {
    setShowFormFlow(false);
    message({ content: t("progressSaved"), icon: "tick" });
    onFormFlowClose?.();
  };

  const completionPct =
    (cardType === "crop"
      ? cropProgressQuery?.data?.completionPct
      : cardType === "dairy"
      ? dairyProgressQuery?.data?.completionPct
      : farmProgressQuery.data?.completionPct) ?? 0;
  const isComplete = isPercentageComplete(completionPct);
  const farmCardCompletion = (farmProgressQuery.data?.completionPct ?? 0) >= 1;
  const preliminaryResult = !!footprintDisplayData?.overall?.perKgYieldCO2e;
  const isSubmittedAndIncomplete = yearIsSubmitted && cardType === "crop" && !isComplete;
  const isCropFormSubmitted = yearIsSubmitted && cardType === "crop";
  const showFeedAddition = cardType === "dairy" && feedTypesWithCompletion.length > 0 && !isSubmittedAndIncomplete;
  const selectedFeedType = feedTypesWithCompletion.find((ft) => ft.feedTypeId === selectedFeedTypeId);

  const openFormFlow = () => {
    if (cardType === "crop") {
      cropPrimaryDataQuery.refetch();
    } else if (cardType === "dairy") {
      dairyPrimaryDataQuery.refetch();
    } else {
      farmPrimaryDataQuery.refetch();
    }
    setShowFormFlow(true);
  };

  const deleteAssessment = () => {
    if (cardType === "crop") {
      deleteFootprintingCropByPrimaryDataId.mutate();
    }
  };

  const contextMenuItems: ItemProps[] = [
    cardType === "crop" && {
      label: (
        <Tooltip placement="right" title={t("formButtons.editDisabledTooltip")} disabled={isCropFormSubmitted}>
          <span>{t("formButtons.button.edit")}</span>
        </Tooltip>
      ),
      onClick: () => openFormFlow(),
      state: isCropFormSubmitted ? "disabled" : undefined,
    },
    cardType === "crop" &&
      isCropFormSubmitted && {
        label: t("formButtons.reviewSubmittedData"),
        onClick: () => openFormFlow(),
      },
    cardType === "crop" &&
      !isCropFormSubmitted && {
        label: <span>{t("formButtons.button.remove")}</span>,
        onClick: () => setShowRemoveAssessmentModal(true),
      },
    cardType === "dairy" && {
      label: (
        <Tooltip placement="right" title={t("formButtons.editDisabledTooltip")} disabled={yearIsSubmitted}>
          <span>{t("formButtons.button.edit")}</span>
        </Tooltip>
      ),
      onClick: () => openFormFlow(),
      state: yearIsSubmitted ? "disabled" : undefined,
    },
    cardType === "dairy" &&
      yearIsSubmitted && {
        label: t("formButtons.reviewSubmittedData"),
        onClick: () => openFormFlow(),
      },
    cardType === "dairy" &&
      !yearIsSubmitted && {
        label: <span>{t("formButtons.button.remove")}</span>,
        onClick: () => setShowRemoveAssessmentModal(true),
      },
  ].filter(Boolean) as ItemProps[];

  const showContextMenuButton = contextMenuItems.length > 0;

  const handleButtonClick = () => {
    const isSubmittedViewResultsButton = completionPct >= 1 && yearIsSubmitted && cardType === "crop";
    const isUnsubmittedViewResultsButton = !yearIsSubmitted && cardType === "crop" && completionPct >= 1 && farmCardCompletion;

    if (isSubmittedViewResultsButton || isUnsubmittedViewResultsButton) {
      navigate("/footprinting/results", { state: { cropId, year } });
    } else {
      openFormFlow();
    }
  };

  const cardContent = (
    <Card
      className={`padding-100 width-100 margin-top-100 footprint-button-card ${isSubmittedAndIncomplete ? "disabled-footprinting-form-button" : ""} ${
        showFeedAddition ? "has-addition" : ""
      }`}
      onClick={handleButtonClick}
    >
      <Flex align="center">
        {(cardType === "crop" || cardType === "dairy") && (
          <div className="crop-image-container margin-right-125">
            <LazyLoadImage
              alt={cardType === "crop" ? `${cropInfo?.name ?? "Crop"} image` : "dairy image"}
              src={
                cardType === "crop"
                  ? cropInfo?.imageURL || "https://klimpublic.s3.eu-central-1.amazonaws.com/commodity-placeholder.webp"
                  : "https://klimpublic.s3.eu-central-1.amazonaws.com/footprinting-crop-images/small/dairy.webp"
              }
              className="footprinting-card-image"
            />
          </div>
        )}
        <CardContentGroup
          grow
          title={cropInfo?.name ?? (cardType === "dairy" ? t("formButtons.dairyButtonTitle") : t("formButtons.farmButtonTitle"))}
          description={cardType === "crop" ? "" : cardType === "dairy" ? "" : t("formButtons.farmButtonDescription")}
          tag={getTagProps(t, yearIsSubmitted ?? false, isComplete, farmCardCompletion, preliminaryResult, cardType, completionPct)}
        />
        {cardType !== "farm" ? (
          <CardContentGroup
            align="right"
            title={isComplete && footprintDisplayData ? `${formatNumber({ number: footprintDisplayData.overall.perKgYieldCO2e, decimalPlaces: 2 })} kg` : "-"}
            description={t("formButtons.totalCo2PerKgYield")}
            tooltipTitle={
              cardType === "crop" ? (yearIsSubmitted ? t("formButtons.submittedTotalCo2Tooltip") : t("formButtons.inProgressTotalCo2Tooltip")) : undefined
            }
          />
        ) : null}
        <div className="margin-left-250 margin-right-100">
          <CardContentGroup align="right" title={`${Math.round(formatFractionInput(completionPct) ?? 0)}%`} description={t("formButtons.completePct")} />
        </div>
        <div className="footprint-button-card-column center margin-left-400">
          <Flex gap={20} align="center">
            <Tooltip
              disabled={!isSubmittedAndIncomplete && !(completionPct >= 1 && yearIsSubmitted)}
              placement="top"
              title={isSubmittedAndIncomplete ? t("formButtons.submittedAndIncompleteTooltip") : undefined}
            >
              <Button
                style={{ width: "fit-content" }}
                type="secondary"
                size="small"
                onClick={handleButtonClick}
                disabled={(completionPct >= 1 && yearIsSubmitted && cardType !== "crop") || isSubmittedAndIncomplete}
              >
                {t(
                  yearIsSubmitted && cardType === "crop"
                    ? "formButtons.button.viewResults"
                    : completionPct < 0.01
                    ? "formButtons.button.start"
                    : completionPct <= 0.99
                    ? "formButtons.button.continue"
                    : !yearIsSubmitted
                    ? cardType === "crop" && farmCardCompletion
                      ? "formButtons.button.viewResults"
                      : "formButtons.button.edit"
                    : "formButtons.button.reviewData"
                )}
              </Button>
            </Tooltip>
          </Flex>
        </div>
        <Flex className="context-menu-container margin-left-100">
          {showContextMenuButton ? (
            <ContextMenu
              items={contextMenuItems}
              placement="bottom-right"
              visible={showContextMenu}
              onClose={() => setShowContextMenu(false)}
              dataTestId="footprinting-crop-context-menu"
              className="footprinting-context-menu"
              iconPlacement="center"
            >
              <Flex
                data-testid="footprinting-crop-context-menu-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowContextMenu(true);
                }}
              >
                <Icon iconName="tabler/dots-vertical" strokeWidth={2} className="pointer" />
              </Flex>
            </ContextMenu>
          ) : (
            <div className="context-menu-placeholder" />
          )}
        </Flex>
      </Flex>
    </Card>
  );

  const additionCard = showFeedAddition && (
    <Card className="footprint-button-card-addition">
      <FormCardButtonAddition
        items={feedTypesWithCompletion.map((feedType) => ({
          id: feedType.feedTypeId,
          name: feedType.name,
          icon: isMainCommodity(feedType.translationSlug) ? "🌾" : "🌱",
          isComplete: feedType.isComplete,
        }))}
        completeText={t("dairyFormButtonAdditionTag.complete")}
        incompleteText={t("dairyFormButtonAdditionTag.incomplete")}
        completeColor="green"
        incompleteColor="grey"
        onItemClick={(feedTypeId) => setSelectedFeedTypeId(feedTypeId)}
      />
    </Card>
  );

  return (
    <>
      {showFeedAddition ? (
        <div className="footprint-button-card-wrapper">
          {cardContent}
          {additionCard}
        </div>
      ) : (
        cardContent
      )}

      {selectedFeedType && (
        <FootprintingDairyFeedProductionDrawer
          farmId={farmId}
          visible={selectedFeedTypeId !== null}
          onClose={() => setSelectedFeedTypeId(null)}
          feedInformationId={selectedFeedType.feedInformationId}
          feedTypeName={selectedFeedType.name}
          feedTypeTranslationSlug={selectedFeedType.translationSlug}
        />
      )}

      <Modal
        showModal={showRemoveAssessmentModal}
        closeIcon={<Icon className="grey" iconName="tabler/x" />}
        onCancel={() => setShowRemoveAssessmentModal(false)}
        onOk={() => deleteAssessment()}
        okText={t("formButtons.button.remove")}
        cancelText={t("modal.cancel")}
        title={t("assessmentManagement.removeAssessmentModal.title", { cropName: cropInfo?.name, year })}
        content={
          <Flex vertical>
            <p>{t("assessmentManagement.removeAssessmentModal.description", { cropName: cropInfo?.name, year })}</p>
            <AlertBanner headingSize="h6" type="#F1F6FF" iconName="tabler/info-circle" description={t("assessmentManagement.removeAssessmentModal.banner")} />
            <Divider />
            <p className="">{t("assessmentManagement.removeAssessmentModal.info")}</p>
          </Flex>
        }
      />
      <ErrorBoundary>
        {cardType === "crop" ? (
          <CropFormFlow
            readOnly={yearIsSubmitted}
            showFormFlow={showFormFlow}
            onExit={handleExit}
            cropId={cropId!}
            farmId={farmId}
            year={year}
            initialStep={initialStep}
          />
        ) : cardType === "dairy" ? (
          <DairyFormFlow readOnly={yearIsSubmitted} showFormFlow={showFormFlow} onExit={handleExit} farmId={farmId} year={year} />
        ) : (
          <FarmFormFlow readOnly={yearIsSubmitted} showFormFlow={showFormFlow} onExit={handleExit} farmId={farmId} year={year} />
        )}
      </ErrorBoundary>
    </>
  );
};

export default FootprintingFormButton;
